import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Program } from "@jackedlogs/shared";

import type { LibraryItem, LibraryState } from "./useLibrary";

// The Library's guarded management writes (HR-S4), extracted from
// LibraryBrowser so that component stays layout + grouping. ONE shape for
// every write: clear the stale error, mark the item busy, surface a
// localized message on failure (the raw api_error_NNN code stays a console
// breadcrumb, never UI copy). Follow keeps its own busy key because the
// card swaps its label to "Following…"; the other writes share one.
export function useLibraryActions(
  lib: Pick<LibraryState, "setProgramStatus" | "deleteItem">,
  /** Host delete-confirm seam (mobile Alert); omitted → the card two-taps. */
  confirmDelete?: (item: LibraryItem, run: () => void) => void,
) {
  const { t } = useTranslation();
  const [actionError, setActionError] = useState<string | null>(null);
  const [followingKey, setFollowingKey] = useState<string | null>(null);
  const [mutatingKey, setMutatingKey] = useState<string | null>(null);

  const runMutation = async (
    item: LibraryItem,
    setBusy: (k: string | null) => void,
    fn: () => Promise<Error | null>,
    tag: string,
    errKey: string,
    errDefault: string,
  ) => {
    setActionError(null);
    setBusy(item.key);
    const err = await fn();
    setBusy(null);
    if (err) {
      console.warn(`[library] ${tag}`, err.message);
      setActionError(t(errKey, { defaultValue: errDefault }));
    }
  };

  const onFollow = (item: LibraryItem) => {
    if (item.source !== "personal" || item.kind !== "program") return;
    if (followingKey) return;
    void runMutation(
      item,
      setFollowingKey,
      () => lib.setProgramStatus(item.program, "active"),
      "follow_failed",
      "library.errFollow",
      "Could not follow the program.",
    );
  };

  const onSetStatus = (item: LibraryItem, status: Program["status"]) => {
    if (item.source !== "personal" || item.kind !== "program") return;
    if (mutatingKey) return;
    void runMutation(
      item,
      setMutatingKey,
      () => lib.setProgramStatus(item.program, status),
      "status_failed",
      "library.errStatus",
      "Could not update the program.",
    );
  };

  const onDeleteItem = (item: LibraryItem) => {
    if (mutatingKey) return;
    void runMutation(
      item,
      setMutatingKey,
      () => lib.deleteItem(item),
      "delete_failed",
      "library.errDelete",
      "Could not delete. Try again.",
    );
  };

  const requestDelete = (item: LibraryItem) => {
    if (confirmDelete) {
      confirmDelete(item, () => onDeleteItem(item));
      return;
    }
    onDeleteItem(item);
  };

  return {
    actionError,
    setActionError,
    followingKey,
    mutatingKey,
    onFollow,
    onSetStatus,
    requestDelete,
  };
}
